import {
  isSection,
  surveyQuestions,
  type AnswerValue,
  type Question,
  type Section,
  type SurveyDefinition,
} from '@voice2me/shared';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { QuestionField } from './QuestionField.js';

export type AnswersState = Record<string, AnswerValue | undefined>;

type Props = {
  definition: SurveyDefinition;
  answers: AnswersState;
  onAnswerChange: (questionId: string, value: AnswerValue | undefined) => void;
  errors?: Record<string, string>;
  disabled?: boolean;
  /**
   * The submit control. In list mode it renders after the questions; in slideshow mode it
   * appears on the final slide. Omitted by the editor preview, which has nothing to submit.
   */
  footer?: ReactNode;
  /**
   * The anonymity notice, shown as the opening slide in slideshow mode so it appears once at the
   * start rather than hovering over every question. Only used by the slideshow layout.
   */
  introBanner?: ReactNode;
  /**
   * Whether the respondent may advance past the opening banner slide — the caller sets this once
   * they have ticked "I understand". `false` keeps the slideshow parked on the banner.
   */
  canAdvance?: boolean;
  /**
   * Fired once when the first question becomes visible to the respondent: on mount in list mode, or
   * when the first question slide is reached in slideshow mode. The caller uses it to start the
   * completion timer so intro/consent/agreement reading time is excluded. Optional — the editor
   * preview omits it.
   */
  onStart?: () => void;
};

/** True once the respondent has given any answer to this question. */
function isAnswered(value: AnswerValue | undefined): boolean {
  // QuestionField clears an emptied answer back to `undefined`, so presence is enough here.
  return value !== undefined;
}

/** Dispatches to the list or slideshow layout; each is its own component so it can own its hooks. */
export function SurveyForm(props: Props) {
  if (props.definition.displayMode === 'slideshow') {
    return <SlideshowSurvey {...props} />;
  }
  return <ListSurvey {...props} />;
}

/** The classic scroll-through form. Submission chrome is passed in as `footer` by the caller. */
function ListSurvey({ definition, answers, onAnswerChange, errors, disabled, footer, onStart }: Props) {
  // Every question is on screen the moment the form mounts, so that is when the first one "appears".
  useEffect(() => {
    onStart?.();
  }, [onStart]);

  const field = (question: Question) => (
    <QuestionField
      key={question.id}
      question={question}
      value={answers[question.id]}
      onChange={(value) => onAnswerChange(question.id, value)}
      error={errors?.[question.id]}
      disabled={disabled}
    />
  );

  return (
    <>
      {definition.questions.map((item) =>
        isSection(item) ? (
          <section key={item.id} className="survey-section">
            <h2 className="survey-section-title">{item.title}</h2>
            {item.description && <p className="survey-section-description">{item.description}</p>}
            {item.questions.map(field)}
          </section>
        ) : (
          field(item)
        ),
      )}
      {footer}
    </>
  );
}

/**
 * The survey's introduction opens the deck on its own slide, and each section contributes an intro
 * slide (its title and description) ahead of its question slides.
 */
type Slide =
  | { kind: 'consent' }
  | { kind: 'intro'; text: string }
  | { kind: 'section'; section: Section }
  | { kind: 'question'; question: Question; number: number };

/** Flattens the definition into slides, numbering the question slides 1..N for the progress copy. */
function buildSlides(definition: SurveyDefinition): Slide[] {
  const slides: Slide[] = [];
  if (definition.description) slides.push({ kind: 'intro', text: definition.description });
  let number = 0;
  for (const item of definition.questions) {
    if (isSection(item)) {
      slides.push({ kind: 'section', section: item });
      for (const question of item.questions) {
        number += 1;
        slides.push({ kind: 'question', question, number });
      }
    } else {
      number += 1;
      slides.push({ kind: 'question', question: item, number });
    }
  }
  return slides;
}

/** The little uppercase progress label shown above each slide. */
function slideLabel(slide: Slide, questionTotal: number): string {
  switch (slide.kind) {
    case 'consent':
      return 'Before you start';
    case 'intro':
      return 'Introduction';
    case 'section':
      return 'Section';
    case 'question':
      return `Question ${slide.number} of ${questionTotal}`;
  }
}

/**
 * One question per slide, with each section announced on its own intro slide first. "Next" advances
 * with a slide animation; the final slide shows the caller's submit control. Required questions must
 * be answered before advancing, so the respondent can't silently skip past them.
 */
function SlideshowSurvey({
  definition,
  answers,
  onAnswerChange,
  errors,
  disabled,
  footer,
  introBanner,
  canAdvance,
  onStart,
}: Props) {
  // The consent banner, when present, is the very first slide — shown once, ahead of everything.
  const slides: Slide[] = introBanner
    ? [{ kind: 'consent' }, ...buildSlides(definition)]
    : buildSlides(definition);
  const questionTotal = surveyQuestions(definition).length;
  const [current, setCurrent] = useState(0);
  // Drives the enter animation and its direction; bumped on every move so the CSS re-runs.
  const [move, setMove] = useState<{ direction: 'forward' | 'back'; nonce: number }>({
    direction: 'forward',
    nonce: 0,
  });
  // Client-side "this is required" nudges, keyed by question id, shown only after a blocked Next.
  const [localErrors, setLocalErrors] = useState<Record<string, string>>({});

  const clamped = Math.min(current, slides.length - 1);
  const slide = slides[clamped];
  const isLast = clamped === slides.length - 1;

  // A failed submit reports errors on questions that may be off-screen; jump to the first one
  // so its message is actually visible.
  const errorSignature = errors ? Object.keys(errors).sort().join(',') : '';
  const lastHandledErrors = useRef('');
  useEffect(() => {
    if (!errors || Object.keys(errors).length === 0) {
      lastHandledErrors.current = '';
      return;
    }
    if (errorSignature === lastHandledErrors.current) return;
    lastHandledErrors.current = errorSignature;
    const firstBad = slides.findIndex((s) => s.kind === 'question' && errors[s.question.id]);
    if (firstBad >= 0 && firstBad !== clamped) {
      setMove((m) => ({ direction: firstBad < clamped ? 'back' : 'forward', nonce: m.nonce + 1 }));
      setCurrent(firstBad);
    }
  }, [errorSignature, errors, slides, clamped]);

  // The timer starts when the respondent first lands on a question slide, so the consent, intro and
  // section slides they page through first don't count against their completion time.
  const questionShownRef = useRef(false);
  useEffect(() => {
    if (!questionShownRef.current && slide?.kind === 'question') {
      questionShownRef.current = true;
      onStart?.();
    }
  }, [slide, onStart]);

  function go(to: number, direction: 'forward' | 'back') {
    setMove((m) => ({ direction, nonce: m.nonce + 1 }));
    setCurrent(to);
  }

  // The respondent must acknowledge the anonymity notice before leaving the opening slide.
  const blockedOnConsent = slide?.kind === 'consent' && !canAdvance;

  function handleNext() {
    if (blockedOnConsent) return;
    if (
      slide?.kind === 'question' &&
      slide.question.required &&
      !isAnswered(answers[slide.question.id])
    ) {
      setLocalErrors((current) => ({
        ...current,
        [slide.question.id]: 'Please answer this question to continue.',
      }));
      return;
    }
    go(clamped + 1, 'forward');
  }

  if (!slide) return null;

  return (
    <div className="slideshow">
      <div className="slideshow-progress" aria-hidden="true">
        <div className="slideshow-progress-bar" style={{ width: `${((clamped + 1) / slides.length) * 100}%` }} />
      </div>
      <p className="slideshow-count">{slideLabel(slide, questionTotal)}</p>

      <div className="slideshow-viewport">
        <div key={move.nonce} className={`slideshow-slide slideshow-slide-${move.direction}`}>
          {slide.kind === 'consent' ? (
            <div className="slideshow-consent">{introBanner}</div>
          ) : slide.kind === 'intro' ? (
            <div className="slideshow-section-intro">
              <p className="description">{slide.text}</p>
            </div>
          ) : slide.kind === 'section' ? (
            <div className="slideshow-section-intro">
              <h2 className="survey-section-title">{slide.section.title}</h2>
              {slide.section.description && (
                <p className="survey-section-description">{slide.section.description}</p>
              )}
            </div>
          ) : (
            <QuestionField
              question={slide.question}
              value={answers[slide.question.id]}
              onChange={(value) => {
                onAnswerChange(slide.question.id, value);
                if (localErrors[slide.question.id]) {
                  setLocalErrors((current) => {
                    const next = { ...current };
                    delete next[slide.question.id];
                    return next;
                  });
                }
              }}
              error={errors?.[slide.question.id] ?? localErrors[slide.question.id]}
              disabled={disabled}
            />
          )}
        </div>
      </div>

      <div className="slideshow-nav">
        <button
          type="button"
          className="slideshow-back"
          onClick={() => go(clamped - 1, 'back')}
          disabled={clamped === 0 || disabled}
        >
          Back
        </button>
        {isLast ? (
          <div className="slideshow-submit">{footer}</div>
        ) : (
          <button type="button" onClick={handleNext} disabled={disabled || blockedOnConsent}>
            Next
          </button>
        )}
      </div>
    </div>
  );
}
